import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFolder } from '../../api/folders.api';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import type { FolderCreateDto, FolderDto } from '@/lib/api/guidelines-client';

import { useTranslation } from 'react-i18next';

import type { CreateFolderParams } from './useCreateFolder.types';

/**
 * Hook to create a new folder using TanStack Query mutation
 *
 * @returns TanStack Query mutation result with create folder functionality
 */
export const useCreateFolder = () => {
  const { t } = useTranslation('knowledge');
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  return useMutation<FolderDto | null, Error, CreateFolderParams>({
    mutationFn: async ({ title, description }: CreateFolderParams) => {
      if (!workspaceId) {
        throw new Error(t('guidelines.workspaceIdIsRequired'));
      }

      const createDto: FolderCreateDto = {
        workspaceId,
        title,
        description,
      };

      return createFolder(createDto);
    },
    onSuccess: (data) => {
      // Invalidate folders query to refetch the list
      void queryClient.invalidateQueries({ queryKey: ['folders', workspaceId] });

      if (data) {
        notificationService.showSuccess(
          t('guidelines.useCreateFolder.folderCreated', { title: data.title }),
          {
            anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          },
        );
      }
    },
    onError: (error) => {
      notificationService.showError(
        error.message || t('guidelines.useCreateFolder.failedToCreateFolder'),
      );
    },
  });
};
